import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import notificationService from '../services/notificationService';

// Create the notification context
const NotificationContext = createContext();

// Custom hook to use the notification context
export const useNotification = () => {
  return useContext(NotificationContext);
};

// Provider component
export const NotificationProvider = ({ children }) => {
  const [activeUINotification, setActiveUINotification] = useState(null); // To store the message for transient UI notification
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications from the API
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Use notificationService instead of fetch with hardcoded URL
      const params = { limit: 50 }; // Can be adjusted as needed
      const data = await notificationService.getNotifications(params);
      
      // Handle the nested structure of notifications
      // API returns: { message, unreadCount, notifications: { success, meta, results: [] } }
      if (data.notifications && data.notifications.results) {
        // Extract the actual notifications array from the nested structure
        setNotifications(data.notifications.results.map(notification => ({
          ...notification,
          // Convert is_read to read for consistency with the UI
          read: notification.is_read === 1,
          // Ensure createdAt is accessible as a Date object
          createdAt: new Date(notification.created_at)
        })));
      } else {
        // Fallback to empty array if structure is unexpected
        console.warn('Unexpected notification data structure:', data);
        setNotifications([]);
      }
    } catch (err) {
      console.error('Fetch notifications error:', err);
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  // Fetch notifications when user changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
    }
  }, [user, fetchNotifications]);



  // Mark a notification as read
  const markAsRead = async (notificationId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use notificationService instead of fetch with hardcoded URL
      await notificationService.markAsRead(notificationId);
      
      // Update the notification in the state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );
    } catch (err) {
      console.error('Mark as read error:', err);
      setError(err.message || 'Failed to mark notification as read');
    } finally {
      setLoading(false);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      // Make API call to mark all notifications as read
      await notificationService.markAllAsRead();
      
      // Update all notifications in the state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => ({
          ...notification,
          read: true
        }))
      );
    } catch (err) {
      console.error('Mark all as read error:', err);
      setError(err.message || 'Failed to mark all notifications as read');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a specific notification
  const deleteNotification = async (id) => {
    if (loading) return;
    
    setLoading(true);
    try {
      // Make API call to delete the notification
      await notificationService.deleteNotification(id);
      
      // Remove the deleted notification from state
      setNotifications(prevNotifications => 
        prevNotifications.filter(notification => notification.id !== id)
      );
    } catch (err) {
      console.error('Delete notification error:', err);
      setError(err.message || 'Failed to delete notification');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete all notifications
  const deleteAllNotifications = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      // Make API call to delete all notifications
      await notificationService.deleteAllNotifications();
      
      // Clear notifications from state
      setNotifications([]);
    } catch (err) {
      console.error('Delete all notifications error:', err);
      setError(err.message || 'Failed to delete all notifications');
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    notifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    // Helper to get unread count
    getUnreadCount: () => {
      if (!Array.isArray(notifications)) return 0;
      return notifications.filter(n => !n.read).length;
    },
    // Function to display a transient UI notification
    showNotification: useCallback((message, type = 'info') => {
      console.log(`[NotificationContext] UI Notification: ${message} (Type: ${type})`);
      setActiveUINotification({ message, type, id: Date.now() });
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setActiveUINotification(null);
      }, 5000);
    }, []),
    activeUINotification, // Make the active UI notification available if a component wants to render it
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
